import {
  TmElement,
  border,
  color,
  rem,
  reset,
} from '@thiememeulenhoff/lit-sdk';
import * as token from '@thiememeulenhoff/tokens/core';
import { IconName } from '@thiememeulenhoff/types';
import { css, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../helpers';

const tag = tagName('toggle-button');

@customElement(tag)
export class ToggleButtonComponent extends TmElement {
  @property({ type: String, attribute: 'label-a' })
  labelA = 'Aan';

  @property({ type: String, attribute: 'label-b' })
  labelB = 'Uit';

  @property({ type: String, attribute: 'icon-a' })
  iconA?: IconName;

  @property({ type: String, attribute: 'icon-b' })
  iconB?: IconName;

  @property({ type: Boolean, reflect: true })
  value = false;

  static override styles = [
    reset.button,
    css`
      :host {
        display: inline-block;
      }

      :where(button) {
        align-items: center;
        background-color: ${color('grey', 300)};
        border-radius: ${rem(8)};
        color: ${color('grey', 900)};
        display: flex;
        font-weight: bold;
        margin-inline-end: ${rem(1)};
        padding: ${rem(8, 16)};
      }

      button:hover:not([active='true']) {
        background: ${color('grey', 400)};
      }

      section {
        background: ${color('grey', 300)};
        border: ${border(2, 'grey', 300)};
        border-radius: ${rem(8)};
        display: flex;
        font-weight: 600;
        inline-size: max-content;
        margin: ${rem(8, 0, 0, 0)};
      }

      @media (max-width: ${unsafeCSS(token.BreakpointSmall)}) {
        section {
          flex-direction: column;
        }
      }

      :where(a-icon) {
        font-size: ${rem(18)};
        padding: ${rem(0, 8, 0, 0)};
      }

      :where(button[active='true']) {
        background-color: ${color('pink', 800)};
        color: ${color('white')};
      }
    `,
  ];

  #iconTemplate(icon: IconName) {
    return html`<a-icon name=${icon}></a-icon>`;
  }

  override render() {
    if (!this.labelA && !this.labelB)
      throw new Error('No labels provided for a-toggle-button component.');

    return html`
      <slot></slot>
      <section data-test="toggle-button">
        <button
          active=${this.value ? true : nothing}
          @click=${this.#handleClick}
          data-test="toggle-button-left"
        >
          ${this.iconA && this.labelA !== ''
            ? this.#iconTemplate(this.iconA)
            : nothing}
          ${this.labelA}
        </button>
        <button
          active=${!this.value ? true : nothing}
          @click=${this.#handleClick}
          data-test="toggle-button-right"
        >
          ${this.iconB && this.labelB !== ''
            ? this.#iconTemplate(this.iconB)
            : nothing}
          ${this.labelB}
        </button>
      </section>
    `;
  }

  #handleClick = (mouseEvent: MouseEvent) => {
    if (
      (mouseEvent.currentTarget as HTMLButtonElement)?.getAttribute('active')
    ) {
      return;
    }

    const updatedValue = !this.value;
    const event = this.dispatchLocalEvent({
      eventName: 'change',
      detail: updatedValue,
      cancelable: true,
    });

    if (event.defaultPrevented) {
      return;
    }

    this.value = updatedValue;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toggle-button': ToggleButtonComponent;
  }
}
